import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import { AdminRoute } from "@/components/auth/AdminRoute";
import { PostHogProvider } from "@/contexts/PostHogProvider";
import { SidebarProvider } from "@/components/ui/sidebar";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import SignUp from "./pages/SignUp";
import SignIn from "./pages/SignIn";
import Explore from "./pages/Explore";
import Franchises from "./pages/Franchises";
import Swipe from "./pages/Swipe";
import Favorites from "./pages/Favorites";
import ListBusiness from "./pages/ListBusiness";
import BusinessDetail from "./pages/BusinessDetail";
import Dashboard from "./pages/Dashboard";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Cookies from "./pages/Cookies";
import Help from "./pages/Help";
import Contact from "./pages/Contact";
import GettingStarted from "./pages/GettingStarted";
import BrokerPortal from "./pages/BrokerPortal";

import PortalSmoke from "./pages/__PortalSmoke";
import AdminSetup from "./pages/AdminSetup";
import AdminUserCleanup from "./pages/AdminUserCleanup";
import AdminDeleteUser from "./pages/AdminDeleteUser";
import AuthCallback from "@/pages/AuthCallback";
import AcceptInvite from "./pages/AcceptInvite";
import { HelmetProvider } from 'react-helmet-async';
const queryClient = new QueryClient();

function PortalShell({ children }: { children: React.ReactNode }) {
  // read header height at runtime and store a CSS var on the shell node
  return (
    <div
      id="portal-shell"
      data-portal-shell
      style={
        // fallback to 200px if header not found
        { 
          // this value will be updated by the script in main.tsx
          // but still gives us a sane fallback
          ['--portal-offset' as any]: '200px'
        }
      }
      className="relative"
    >
      {children}
    </div>
  );
}

const App = () => {
  console.log("App component loaded");

  // Forward any Supabase auth hash on any route to the dedicated callback page
  useEffect(() => {
    const hash = window.location.hash ? window.location.hash.substring(1) : '';
    if (!hash) return;
    const params = new URLSearchParams(hash);
    const keys = ['access_token', 'refresh_token', 'error', 'error_code', 'provider_token'];
    const hasAuthHash = keys.some((k) => params.has(k));
    if (hasAuthHash && window.location.pathname !== '/auth/callback') {
      window.location.replace(`/auth/callback${window.location.hash}`);
    }
  }, []);

  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <HelmetProvider>
        <BrowserRouter>
        <PostHogProvider>
          <ScrollToTop />
          <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/explore/page/:page" element={<Explore />} />
          <Route path="/explore/page/:page/" element={<Explore />} />
          <Route path="/franchises" element={<Franchises />} />
          <Route path="/swipe" element={<Swipe />} />
          <Route path="/favorites" element={<Favorites />} />
          <Route path="/list-business" element={<ListBusiness />} />
          <Route path="/business/:id" element={<BusinessDetail />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/privacy-policy" element={<Privacy />} />
          <Route path="/cookie-policy" element={<Cookies />} />
          <Route path="/help" element={<Help />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/getting-started" element={<GettingStarted />} />
          <Route 
            path="/broker-portal" 
            element={
              <SidebarProvider>
                <PortalShell>
                  {window.location.search.includes("debug=1")
                    ? <PortalSmoke />
                    : <BrokerPortal />}
                </PortalShell>
              </SidebarProvider>
            } 
          />

<Route path="/admin-setup" element={<AdminRoute><AdminSetup /></AdminRoute>} />
        <Route path="/admin-delete-user" element={<AdminRoute><AdminDeleteUser /></AdminRoute>} />
        <Route path="/admin-user-cleanup" element={<AdminRoute><AdminUserCleanup /></AdminRoute>} />
        <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </PostHogProvider>
        </BrowserRouter>
      </HelmetProvider>
    </TooltipProvider>
  </QueryClientProvider>
);
};

export default App;
